import React, { useState } from 'react'
import { getAPIBaseURL } from '../services/helpers'
import { postDataWithForm } from '../services/request'

const DOC_ACCEPT =
  '.pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png'

const ApplicationDocumentUpload = ({
  applicationId,
  label,
  isMandatory = true,
  uploadedDoc,
  onUploaded,
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const hasUpload = Boolean(uploadedDoc?.url)

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !applicationId) return

    setUploading(true)
    setError('')
    const formData = new FormData()
    formData.append('file', file)
    formData.append('label', label)

    const url = `${getAPIBaseURL()}/employme/jobseeker/applications/${applicationId}/documents`
    const res = await postDataWithForm(url, {}, formData)
    setUploading(false)
    e.target.value = ''

    if (res.error === '' && (res.result?.status === 200 || res.result?.status === 201)) {
      const data = res.result?.data?.data
      onUploaded?.(data?.document || { label, url: data?.documents?.find((d) => d.label === label)?.url })
    } else {
      setError(res.result?.data?.data || 'Failed to upload document. Please try again.')
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-slate-900">{label}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {isMandatory ? 'Required' : 'Optional'} · PDF, DOC, DOCX, JPG, or PNG (max 10 MB)
          </p>
        </div>
        {isMandatory && (
          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
            Required
          </span>
        )}
      </div>

      {hasUpload ? (
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-emerald-800">Uploaded</p>
            {uploadedDoc.fileName ? (
              <p className="truncate text-xs text-emerald-700">{uploadedDoc.fileName}</p>
            ) : null}
          </div>
          {uploadedDoc.url ? (
            <a
              href={uploadedDoc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-emerald-800 underline hover:text-emerald-900"
            >
              View file
            </a>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3">
        <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100">
          <input
            type="file"
            accept={DOC_ACCEPT}
            className="hidden"
            disabled={disabled || uploading || !applicationId}
            onChange={handleFileChange}
          />
          {uploading ? 'Uploading…' : hasUpload ? 'Replace file' : 'Choose file'}
        </label>
      </div>

      {error ? <p className="mt-2 text-xs font-medium text-red-600">{error}</p> : null}
    </div>
  )
}

export const getMissingMandatoryDocuments = (job, application) => {
  const required = job?.screening?.requiredDocuments || job?.jobId?.screening?.requiredDocuments || []
  const mandatory = required.filter((d) => d && d.label && d.isMandatory !== false)
  if (!mandatory.length) return []

  const uploadedLabels = new Set(
    (application?.documents || [])
      .filter((d) => d?.url)
      .map((d) => String(d.label || '').trim().toLowerCase())
  )

  return mandatory.filter((req) => !uploadedLabels.has(String(req.label || '').trim().toLowerCase()))
}

export default ApplicationDocumentUpload
